// Task Calendar — properties × days resource timeline.
//
// Visual design adapted from the prototype on `nars/tasks-v2`
// (sticky property column, day grid, lane-allocated pills, popup
// detail card, drag-and-drop). The data however is real:
//   - tasks/work-orders are pulled from GET /api/v2/workorders
//     for the visible date window
//   - guest/owner blocks are pulled from POST /api/v2/reservations/search
//     (categories STA + OWN)
//
// Drag-and-drop persists schedule changes for one-off tasks. Recurring
// tasks prompt for single-occurrence vs all-future scope (virtual pills
// are materialized before updating).
//
// Empty-cell click seeds the shared WorkOrderContext with the clicked
// rental_id + scheduled_date and opens the existing #work-order-add
// modal (the same one used on the /operations/tasks page) so the user
// can quickly add a task at that slot.
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import UIkit from "uikit";
import { AppContexts } from "../../../providers";
import TaskOccurrencesService from "../../../services/taskOccurrences.service";
import TaskRecurrenceRuleService from "../../../services/taskRecurrenceRule.service";
import WorkOrderService from "../../../services/workorder.service";
import ReservationsService from "../../../services/reservations.service";
import WorkOrderAdd from "../work_order_add";
import WorkOrderDelete from "../work_order_delete";
import { buildVirtualOccurrenceWorkOrder } from "./calendarOccurrenceHelpers";
import {
  buildDragFutureSplitPayload,
  isRecurringWorkOrder,
  promptRecurringEditScope,
} from "../recurringScopePrompt";
import { openWorkOrderAddPanel } from "../workOrderAddPanel";
import { PRIORITY_OPTIONS, PRIORITY_LABELS, getPriorityLabel } from "../priorityOptions";

// ---- date helpers ----------------------------------------------------------
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function asYMD(input) {
  if (!input) return null;
  const m = String(input).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return ymdLocal(d);
}
function addDaysISO(dateStr, n) {
  const d = new Date(`${asYMD(dateStr)}T00:00:00`);
  d.setDate(d.getDate() + n);
  return ymdLocal(d);
}
function getDaysArray(startDate, daysCount) {
  const arr = [];
  const d = new Date(`${asYMD(startDate)}T00:00:00`);
  for (let i = 0; i < daysCount; i++) {
    arr.push(ymdLocal(d));
    d.setDate(d.getDate() + 1);
  }
  return arr;
}
function clamp(n, min, max) {
  return Math.max(min, Math.min(n, max));
}
function dayIndexFromClientX(e, rowEl, stickyWidth, dayWidth, dayCount) {
  const rect = rowEl.getBoundingClientRect();
  const x = e.clientX - rect.left - stickyWidth;
  const raw = Math.floor(x / dayWidth);
  return clamp(raw, 0, dayCount - 1);
}
function isAfternoon(s) {
  const d = new Date(s);
  return d.getHours() >= 12;
}
function isMorning(s) {
  const d = new Date(s);
  return d.getHours() < 12;
}
function clampPopupPosition(x, y, w = 360, h = 280, pad = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(pad, Math.min(x, vw - w - pad)),
    y: Math.max(pad, Math.min(y, vh - h - pad)),
  };
}
function popupPositionAwayFromRect(rect, w = 360, h = 280, gap = 14) {
  const pad = 12;
  const vw = window.innerWidth;
  const rightX = rect.right + gap;
  const leftX = rect.left - w - gap;
  let x = rightX + w <= vw - pad ? rightX : leftX;
  let y = rect.top - 8;

  if (x < pad) {
    x = clamp(rect.left, pad, vw - w - pad);
    y = rect.bottom + gap;
    if (y + h > window.innerHeight - pad) {
      y = rect.top - h - gap;
    }
  }

  return clampPopupPosition(x, y, w, h, pad);
}
function formatDateRange(start, end) {
  const sd = new Date(`${start}T00:00:00`);
  const ed = new Date(`${end}T00:00:00`);
  const sameMonth = sd.getMonth() === ed.getMonth();
  const sameYear = sd.getFullYear() === ed.getFullYear();
  const startStr = sd.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const endStr = ed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return sameMonth && sameYear
    ? `${startStr} – ${ed.getDate()}, ${ed.getFullYear()}`
    : `${startStr} – ${endStr}`;
}

function titleizeValue(value) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatPopupDateTime(value) {
  if (!value) return "";
  const datePart = asYMD(value);
  if (!datePart) return "";
  const dateLabel = new Date(`${datePart}T00:00:00`).toLocaleDateString();
  const timeMatch = String(value).match(/[T\s](\d{2}:\d{2})(?::\d{2})?/);
  if (!timeMatch || timeMatch[1] === "00:00") return dateLabel;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return dateLabel;
  const timeLabel = parsed.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateLabel} ${timeLabel}`;
}

// ---- visual config ---------------------------------------------------------
const ACTIVITY_META = {
  guest: {
    className: "calendar-item-guest",
    label: "Guest",
    icon: "/images/guest-white.svg",
    popupIcon: "/images/guest-active.svg",
  },
  owner: {
    className: "calendar-item-owner",
    label: "Owner",
    icon: "/images/owner-white.svg",
    popupIcon: "/images/owner-active.svg",
  },
  maintenance: {
    className: "calendar-item-maintenance",
    label: "Maintenance",
    icon: "/images/maintenance-white.svg",
    popupIcon: "/images/maintenance-active.svg",
  },
  housekeeping: {
    className: "calendar-item-housekeeping",
    label: "Clean",
    icon: "/images/housekeeping-white.svg",
    popupIcon: "/images/housekeeper-active.svg",
  },
  inspection: {
    className: "calendar-item-task",
    label: "Inspection",
    icon: "/images/task-white.svg",
    popupIcon: "/images/task.svg",
  },
  task: {
    className: "calendar-item-task",
    label: "Task",
    icon: "/images/task-white.svg",
    popupIcon: "/images/task.svg",
  },
};

const STICKY_COL_WIDTH = 260;
const DAY_COL_WIDTH = 142;
const LANE_HEIGHT = 30;
// Minimum row height so the sticky property label + address are never clipped.
const MIN_PROPERTY_ROW_HEIGHT = 56;
const MIN_PROPERTY_ROW_HEIGHT_NO_ADDRESS = 40;

function propertyRowMinHeight(home) {
  if (!home?.address) return MIN_PROPERTY_ROW_HEIGHT_NO_ADDRESS;
  const estLabelLines = Math.max(1, Math.ceil(String(home.label || "").length / 32));
  const estAddressLines = Math.max(1, Math.ceil(String(home.address || "").length / 34));
  const padding = 22;
  const labelLine = 19;
  const addressLine = 17;
  const gap = 2;
  const estimated =
    padding + estLabelLines * labelLine + gap + estAddressLines * addressLine;
  return Math.max(MIN_PROPERTY_ROW_HEIGHT, estimated);
}

const TYPE_LABELS = {
  clean: "Clean",
  inspection: "Inspection",
  maintenance: "Maintenance",
  task: "Task",
  guest: "Guest",
  owner: "Owner",
};

const VIEW_MAP = {
  Today: 1,
  "3-day": 3,
  "7-day": 7,
  "14-day": 14,
  "30-day": 30,
};

// Map a backend work-order task_type to the calendar's visual activity
// type. Per spec the only canonical types are Cleaning / Inspection /
// Maintenance / Other, so anything that isn't cleaning/inspection/
// maintenance falls into the generic "task" lane.
function workOrderToActivityType(taskType) {
  switch ((taskType || "").toLowerCase()) {
    case "cleaning":
      return "housekeeping";
    case "inspection":
      return "inspection";
    case "maintenance":
    case "pool_service":
      return "maintenance";
    default:
      return "task";
  }
}

function assigneeDisplayName(assignee) {
  if (!assignee) return "";
  if (assignee.assignee_name) return assignee.assignee_name;
  if (assignee.first_name || assignee.last_name) {
    return `${assignee.first_name || ""} ${assignee.last_name || ""}`.trim();
  }
  return assignee.name || assignee.email || "";
}

function pickAssigneeNames(wo) {
  const list = wo.work_order_assignees;
  if (!Array.isArray(list) || list.length === 0) return "";
  return list
    .filter((assignee) => assignee.assignment_role !== "owner")
    .map(assigneeDisplayName)
    .filter(Boolean)
    .join(", ");
}

function pickPropertyManagerName(wo) {
  const list = wo.work_order_assignees;
  if (!Array.isArray(list) || list.length === 0) return "";
  return assigneeDisplayName(
    list.find((assignee) => assignee.assignment_role === "owner")
  );
}

function isAssignableActivity(activity) {
  return activity && !["guest", "owner"].includes(activity.type);
}

// ---- component -------------------------------------------------------------
const WorkOrderCalendar = () => {
  const rentalContext = useContext(AppContexts.RentalsContext);
  const { rentals, loading: rentalsLoading, get: getRentals } = rentalContext;
  const workOrderContext = useContext(AppContexts.WorkOrderContext);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalMounted, setAddModalMounted] = useState(false);
  const [shouldOpenAddModal, setShouldOpenAddModal] = useState(false);
  const [activeView, setActiveView] = useState("7-day");
  const [dateWindow, setDateWindow] = useState(() => ({
    start: ymdLocal(new Date()),
    days: 7,
  }));
  const [popupData, setPopupData] = useState(null);
  const [criteria, setCriteria] = useState({
    type: "",
    priority: "",
    assignee: "",
    property: "",
    search_term: "",
  });
  const draggedTaskRef = useRef(null);
  const rentalsRequestedRef = useRef(false);
  const popupHoverTimerRef = useRef(null);
  const filterDropdownRef = useRef(null);

  const [filterDraft, setFilterDraft] = useState({
    type: "",
    priority: "",
    property: "",
  });

  const clearPopupTimer = useCallback(() => {
    if (popupHoverTimerRef.current) {
      clearTimeout(popupHoverTimerRef.current);
      popupHoverTimerRef.current = null;
    }
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((c) => ({ ...c, [key]: value }));
  }, []);

  useEffect(() => {
    setFilterDraft({
      type: criteria.type || "",
      priority: criteria.priority || "",
      property: criteria.property || "",
    });
  }, [criteria.type, criteria.priority, criteria.property]);

  const applyFilterDraft = () => {
    setCriteria((prev) => ({ ...prev, ...filterDraft }));
    if (filterDropdownRef.current) {
      UIkit.dropdown(filterDropdownRef.current).hide(false);
    }
  };

  const clearFilterDraft = () => {
    setFilterDraft({ type: "", priority: "", property: "" });
  };

  const clearAppliedFilters = () => {
    setCriteria((prev) => ({
      ...prev,
      type: "",
      priority: "",
      property: "",
    }));
  };

  // homes derived from real rentals; preserves the v2 shape
  // ({ id, label, address }) so the rest of the layout code is unchanged.
  const homes = useMemo(() => {
    const list = rentals || [];
    return list.map((r) => ({
      id: String(r.rental_id),
      label: r.name || r.pms_id || `Rental ${r.rental_id}`,
      address: r.address || "",
      rental_id: r.rental_id,
      pms_id: r.pms_id,
    }));
  }, [rentals]);

  const propertyById = useMemo(() => {
    const map = {};
    for (const h of homes) map[h.id] = h;
    return map;
  }, [homes]);

  const selectedPropertyLabel = criteria.property
    ? propertyById[String(criteria.property)]?.label || "All Properties"
    : "All Properties";
  const selectedTypeLabel = criteria.type
    ? TYPE_LABELS[criteria.type] || "All Types"
    : "All Types";
  const selectedPriorityLabel = criteria.priority
    ? PRIORITY_LABELS[criteria.priority] || "All Priorities"
    : "All Priorities";
  const visibleHomes = useMemo(() => {
    if (!criteria.property) return homes;
    return homes.filter((home) => String(home.id) === String(criteria.property));
  }, [homes, criteria.property]);

  // ---- data load ----------------------------------------------------------
  const days = useMemo(
    () => getDaysArray(dateWindow.start, dateWindow.days),
    [dateWindow.start, dateWindow.days]
  );
  const windowStartISO = days[0];
  const windowEndISO = addDaysISO(days[0], days.length); // exclusive

  const normalizeWorkOrderBatch = (woData) => {
    if (Array.isArray(woData)) return woData;
    let paginated =
      woData?.paginated || woData?.workorders || woData?.data || [];
    if (typeof paginated === "string") {
      try {
        paginated = JSON.parse(paginated);
      } catch (_) {
        paginated = [];
      }
    }
    return Array.isArray(paginated) ? paginated : [];
  };

  const load = useCallback(async () => {
    if (!windowStartISO || !windowEndISO) return;
    setLoading(true);
    try {
      const dateFrom = windowStartISO;
      const dateTo = addDaysISO(windowEndISO, -1);
      const occRes = await TaskOccurrencesService.list({
        date_from: dateFrom,
        date_to: dateTo,
      }).catch((e) => {
        console.error("task-occurrences fetch failed", e);
        return { data: { occurrences: [] } };
      });
      const occurrences = occRes?.data?.occurrences || [];
      const resRes = await ReservationsService.searchReservation({
        start_date: dateFrom,
        end_date: dateTo,
        preset: "staying-between",
        filters: "standard,owners",
        categories: ["STA", "OWN"],
        page: 0,
        per_page: 500,
      }).catch((e) => {
        console.error("reservations fetch failed", e);
        return { data: { reservations: [] } };
      });

      const reservations =
        resRes?.data?.reservations ||
        resRes?.data?.data ||
        resRes?.data ||
        [];

      const reservationById = new Map(
        (Array.isArray(reservations) ? reservations : [])
          .map((reservation) => [String(reservation.guest_reservation_id), reservation])
      );

      const woActivities = (Array.isArray(occurrences) ? occurrences : [])
        .filter((occ) => !!occ.scheduled_date)
        .map((occ) => ({
          type: workOrderToActivityType(occ.task_type),
          homeId: String(occ.rental_id || ""),
          label: occ.summary || occ.task_type || "Task",
          date: String(occ.scheduled_date).slice(0, 10),
          scheduledDate: occ.scheduled_date,
          status: occ.kind === "virtual" ? undefined : occ.status,
          priority: occ.priority,
          assignee: occ.kind === "concrete"
            ? pickAssigneeNames({ work_order_assignees: occ.work_order_assignees })
            : (occ.assignees || []).map((a) => a.name || a.id).join(", "),
          propertyManager: occ.kind === "concrete"
            ? pickPropertyManagerName(occ)
            : "",
          category: occ.category,
          taskType: occ.task_type,
          id: occ.work_order_id || occ.occurrence_key,
          taskId: occ.work_order_id || null,
          workOrderId: occ.work_order_id || null,
          guestReservationId: occ.guest_reservation_id || null,
          scheduledStartTime: occ.scheduled_start_time || null,
          scheduledEndTime: occ.scheduled_end_time || null,
          ticket_number: occ.ticket_number,
          isRecurring: !!occ.is_recurring,
          isVirtual: occ.kind === "virtual",
          kind: occ.kind,
          occurrenceKey: occ.occurrence_key,
          taskRecurrenceRuleId: occ.rule_id || null,
          taskSource: occ.task_source || (occ.is_recurring ? "recurrence" : null),
          guestReservation: occ.guest_reservation_id
            ? reservationById.get(String(occ.guest_reservation_id)) || null
            : null,
        }));

      const resActivities = (Array.isArray(reservations) ? reservations : [])
        .filter((r) => r.rental_id && r.check_in && r.check_out)
        .filter((r) => r.category === "STA" || r.category === "OWN")
        .map((r) => ({
          type: r.category === "OWN" ? "owner" : "guest",
          homeId: String(r.rental_id),
          label:
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            r.guest_name ||
            "Reservation",
          checkIn: `${asYMD(r.check_in)}T15:00:00`,
          checkOut: `${asYMD(r.check_out)}T10:00:00`,
          guestName:
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            r.guest_name ||
            "",
          reservationId: r.reservation_id || r.guest_reservation_id,
          source: r.source,
          formatted_phone: r.formatted_phone,
        }));

      setActivities([...woActivities, ...resActivities]);
    } finally {
      setLoading(false);
    }
  }, [windowStartISO, windowEndISO, dateWindow.days]);

  const applyDraggedTaskDate = useCallback(
    async (dragged, droppedDate) => {
      const patchLocalDate = () => {
        setActivities((prev) =>
          prev.map((item) =>
            item.id === dragged.id
              ? { ...item, date: droppedDate, scheduledDate: droppedDate }
              : item
          )
        );
      };

      if (!dragged.isRecurring) {
        if (!dragged.workOrderId) return;
        patchLocalDate();
        try {
          await WorkOrderService.updateSchedule(dragged.workOrderId, {
            scheduled_date: droppedDate,
          });
          await load();
        } catch (err) {
          console.error("Failed to update task date", err);
          UIkit.notification("Failed to move task", "danger");
          load();
        }
        return;
      }

      const scope = await promptRecurringEditScope({
        itemLabel: "Task",
        summary: dragged.label,
        intent: "move",
      });
      if (!scope) return;

      const ruleId = dragged.taskRecurrenceRuleId;
      if (!ruleId) {
        UIkit.notification(
          "Unable to identify recurrence rule for this task.",
          "danger"
        );
        return;
      }

      if (scope === "this") {
        try {
          let workOrderId = dragged.workOrderId;
          if (dragged.isVirtual) {
            if (!dragged.occurrenceKey) {
              throw new Error("missing occurrence key");
            }
            const matRes = await TaskOccurrencesService.materialize({
              rule_id: ruleId,
              occurrence_key: dragged.occurrenceKey,
            });
            workOrderId = matRes?.data?.work_order?.id;
            if (!workOrderId) {
              throw new Error("materialize returned no work order");
            }
          }
          await WorkOrderService.updateSchedule(workOrderId, {
            scheduled_date: droppedDate,
          });
          patchLocalDate();
          await load();
          UIkit.notification("Task moved", "success");
        } catch (err) {
          console.error("Failed to move recurring occurrence", err);
          UIkit.notification("Failed to move task", "danger");
          load();
        }
        return;
      }

      try {
        const ruleRes = await TaskRecurrenceRuleService.get(ruleId);
        const rule = ruleRes?.data?.rule || ruleRes?.data;
        const dragPayload = buildDragFutureSplitPayload(rule, dragged.date, droppedDate);

        if (scope === "all") {
          await TaskRecurrenceRuleService.update(ruleId, {
            edit_mode: "all",
            ...dragPayload,
          });
          await load();
          UIkit.notification("All upcoming occurrences updated", "success");
          return;
        }

        await TaskRecurrenceRuleService.split(ruleId, {
          effective_date: dragged.date,
          payload: dragPayload,
        });
        await load();
        UIkit.notification("All future occurrences updated", "success");
      } catch (err) {
        console.error("Failed to move future occurrences", err);
        UIkit.notification(
          scope === "all"
            ? "Failed to update upcoming occurrences"
            : "Failed to update future occurrences",
          "danger"
        );
        load();
      }
    },
    [load]
  );

  useEffect(() => {
    document.title = "Task Calendar - RueBaRue";
  }, []);

  useEffect(() => {
    if (
      !rentals?.length &&
      !rentalsLoading &&
      !rentalsRequestedRef.current &&
      getRentals
    ) {
      rentalsRequestedRef.current = true;
      getRentals();
    }
  }, [getRentals, rentals?.length, rentalsLoading]);

  useEffect(() => {
    load();
  }, [load]);

  // popup dismissal
  useEffect(() => {
    if (!popupData) return undefined;
    const onKey = (e) => e.key === "Escape" && setPopupData(null);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [popupData]);

  // ensure timer is cleared on unmount
  useEffect(() => () => clearPopupTimer(), [clearPopupTimer]);

  // ---- filtering ----------------------------------------------------------
  const filteredActivities = useMemo(() => {
    let list = activities;
    const t = (criteria.type || "").trim();
    if (t) {
      list = list.filter((a) => {
        if (t === "clean") return a.type === "housekeeping";
        if (t === "task") return a.type === "task";
        if (t === "inspection") return a.type === "inspection";
        if (t === "maintenance") return a.type === "maintenance";
        if (t === "guest") return a.type === "guest";
        if (t === "owner") return a.type === "owner";
        return true;
      });
    }
    if (criteria.property) {
      list = list.filter((a) => String(a.homeId) === String(criteria.property));
    }
    if (criteria.priority) {
      // reservations don't have priorities; only filter task-like activities
      list = list.filter((a) => {
        if (a.type === "guest" || a.type === "owner") return false;
        return (a.priority || "").toLowerCase() === criteria.priority;
      });
    }
    if (criteria.assignee) {
      const q = criteria.assignee.toLowerCase();
      list = list.filter((a) => (a.assignee || "").toLowerCase().includes(q));
    }
    if (criteria.search_term) {
      const q = criteria.search_term.toLowerCase();
      list = list.filter(
        (a) =>
          (a.label || "").toLowerCase().includes(q) ||
          (a.guestName || "").toLowerCase().includes(q) ||
          String(a.reservationId || "")
            .toLowerCase()
            .includes(q) ||
          String(a.ticket_number || "")
            .toLowerCase()
            .includes(q)
      );
    }
    return list;
  }, [activities, criteria]);

  const activitiesByHome = useMemo(() => {
    const map = {};
    homes.forEach((h) => {
      map[h.id] = [];
    });
    filteredActivities.forEach((a) => {
      (map[a.homeId] ??= []).push(a);
    });
    return map;
  }, [homes, filteredActivities]);

  // ---- window helpers ----------------------------------------------------
  function overlapsWindow(inISO, outISO) {
    const start = inISO;
    const end = outISO || addDaysISO(inISO, 1);
    return !(end <= windowStartISO || start >= windowEndISO);
  }
  function indexInWindow(iso) {
    const i = days.indexOf(iso);
    if (i !== -1) return i;
    return iso < windowStartISO ? 0 : days.length;
  }
  function moveWindow(daysDelta) {
    setDateWindow((w) => ({ ...w, start: addDaysISO(w.start, daysDelta) }));
  }

  const openWorkOrderModal = () => {
    setAddModalMounted(true);
    setShouldOpenAddModal(true);
  };

  useEffect(() => {
    if (!shouldOpenAddModal || !addModalMounted) return;
    setShouldOpenAddModal(false);
    openWorkOrderAddPanel();
  }, [addModalMounted, shouldOpenAddModal]);

  // ---- empty-cell click -> open Add modal --------------------------------
  // We seed the shared WorkOrderContext with rental_id + scheduled_date
  // so the WorkOrderAdd form picks up the prefill; then we trigger the
  // existing UIkit modal.
  const openAddForCell = (rentalId, iso) => {
    clearPopupTimer();
    setPopupData(null);
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(true);
    workOrderContext.setWorkOrder({
      account_id: 0,
      guest_reservation_id: 0,
      rental_id: Number(rentalId),
      scheduled_date: iso,
      message_id: 0,
      created_by: 0,
      ticket_number: 0,
      status: "new",
      priority: "medium",
      summary: "",
      description: "",
    });
    openWorkOrderModal();
  };

  const openAddTask = () => {
    clearPopupTimer();
    setPopupData(null);
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(true);
    workOrderContext.initWorkOrder();
    openWorkOrderModal();
  };

  const handleEmptyRowClick = (home) => (e) => {
    if (draggedTaskRef.current || e.defaultPrevented) return;
    if (
      e.target.closest(
        ".calendar-item, .calendar-cell-sticky, button, a, input, select, textarea"
      )
    ) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < STICKY_COL_WIDTH || x > STICKY_COL_WIDTH + days.length * DAY_COL_WIDTH) {
      return;
    }

    const dayIndex = dayIndexFromClientX(
      e,
      e.currentTarget,
      STICKY_COL_WIDTH,
      DAY_COL_WIDTH,
      days.length
    );
    openAddForCell(home.rental_id, days[dayIndex]);
  };

  // ---- pill interactions -------------------------------------------------
  // Hover shows the read-only popup; click opens the edit modal.
  const handlePillHover = (activity) => (e) => {
    if (draggedTaskRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const { x, y } = popupPositionAwayFromRect(rect);
    clearPopupTimer();
    setPopupData({ activity, position: { x, y } });
  };

  const handlePillLeave = () => {
    clearPopupTimer();
    popupHoverTimerRef.current = setTimeout(() => setPopupData(null), 120);
  };

  // Open the existing #work-order-add modal in edit mode for the
  // given activity (must be a task/work-order; reservations are skipped).
  const isRecurringActivity = (activity) =>
    Boolean(activity?.isRecurring) ||
    isRecurringWorkOrder({
      isVirtual: activity?.isVirtual,
      task_recurrence_rule_id: activity?.taskRecurrenceRuleId,
      task_source: activity?.taskSource,
    });

  const openEditForActivity = async (activity) => {
    if (!activity) return;
    if (activity.type === "guest" || activity.type === "owner") return;

    workOrderContext.setRecurringEditScope?.(null);
    if (isRecurringActivity(activity)) {
      const scope = await promptRecurringEditScope({
        itemLabel: "Task",
        summary: activity.label,
        intent: "open",
      });
      if (!scope) return;
      workOrderContext.setRecurringEditScope?.(scope);
    }

    workOrderContext.setIsNew(false);
    const occurrenceDate =
      asYMD(activity.scheduledDate) || asYMD(activity.date) || null;

    if (activity.isVirtual) {
      workOrderContext.setWorkOrder(
        buildVirtualOccurrenceWorkOrder(
          activity,
          occurrenceDate,
          activity.guestReservation || null,
        )
      );
      openWorkOrderModal();
      return;
    }

    if (!activity.workOrderId) return;

    workOrderContext.setWorkOrder((prev) => ({
      ...(prev || {}),
      id: activity.workOrderId,
      scheduled_date: occurrenceDate || prev?.scheduled_date || null,
      task_recurrence_rule_id: activity.taskRecurrenceRuleId || null,
      task_source: activity.taskSource || null,
    }));
    try {
      await workOrderContext.getWorkOrder(activity.workOrderId);
    } catch (_) {
      // Open anyway; the modal can still recover by re-fetching before submit.
    }
    openWorkOrderModal();
  };

  const handlePillClick = (activity) => (e) => {
    e.stopPropagation();
    clearPopupTimer();
    setPopupData(null);
    void openEditForActivity(activity);
  };

  if (!homes.length && loading) {
    return <div className="uk-padding">Loading calendar…</div>;
  }

  return (
    <>
      <div className="calendar-wrapper">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Task Calendar</h1>
        </div>

        <div id="hms-main-body" className="calendar-content-container">
          <div className="hms-guest-btn calendar-add-task-btn">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              onClick={openAddTask}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Task
            </button>
          </div>

          <form className="uk-form hms-form workorder-form-top-filters calendar-toolbar-form">
            <div className="workorder-form-top-filters calendar-toolbar">
              <div className="hms-form hms-form-inline calendar-toolbar-main">
                <div className="uk-inline hms-search-inline calendar-search-inline">
                  <div className="uk-search uk-search-default">
                    {criteria.search_term ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        className="uk-form-icon-flip uk-form-icon"
                        uk-icon="icon: close"
                        onClick={() => handleCriteriaChange("search_term", "")}
                        style={{ background: "transparent", border: 0 }}
                      />
                    ) : (
                      <span
                        className="uk-search-icon-flip"
                        uk-search-icon="true"
                      />
                    )}
                    <input
                      className="uk-search-input"
                      type="search"
                      placeholder="# or Summary"
                      value={criteria.search_term}
                      onChange={(e) =>
                        handleCriteriaChange("search_term", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="hms-filters-btn calendar-filters-btn">
                  <button className="filter" type="button">
                    <img src="/images/filter.svg" alt="Filter" uk-tooltip="Filter" />
                    <span uk-icon="icon: filter; ratio: 0.9"></span>
                  </button>

                  <div
                    ref={filterDropdownRef}
                    uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400"
                    className="hms-filters-dropdown calendar-filters-dropdown"
                  >
                    <div className="uk-form-stacked">
                      <div className="uk-margin-small">
                        <label className="uk-form-label">Type</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.type}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({ ...prev, type: e.target.value }))
                            }
                          >
                            <option value="">All Types</option>
                            <option value="clean">Clean</option>
                            <option value="inspection">Inspection</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="task">Task</option>
                            <option value="guest">Guest</option>
                            <option value="owner">Owner</option>
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Priority</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.priority}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({ ...prev, priority: e.target.value }))
                            }
                          >
                            <option value="">All Priorities</option>
                            {PRIORITY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Property</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.property}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({ ...prev, property: e.target.value }))
                            }
                          >
                            <option value="">All Properties</option>
                            {homes.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="hms-filter-actions uk-margin-small-top">
                        <button
                          type="button"
                          className="uk-button hms-btn hms-red-btn"
                          onClick={applyFilterDraft}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="uk-button hms-btn hms-white-btn"
                          onClick={clearFilterDraft}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="calendar-date-controls">
                  <div className="uk-button-group calendar-view-toggle">
                    {Object.keys(VIEW_MAP).map((label) => (
                      <button
                        key={label}
                        className={`checklist-item-type-btn uk-margin-small-right ${activeView === label ? "active" : ""}`}
                        type="button"
                        onClick={() => {
                          setActiveView(label);
                          setDateWindow((w) => ({
                            start:
                              label === "Today" ? ymdLocal(new Date()) : w.start,
                            days: VIEW_MAP[label],
                          }));
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="uk-flex uk-flex-middle calendar-date-nav">
                    <button
                      className="uk-button uk-button-default uk-button-small"
                      onClick={() => moveWindow(-dateWindow.days)}
                      type="button"
                      uk-tooltip="Previous"
                    >
                      <span uk-icon="chevron-left"></span>
                    </button>

                    <div className="uk-text-bold calendar-date-range">
                      {formatDateRange(
                        dateWindow.start,
                        addDaysISO(dateWindow.start, dateWindow.days - 1)
                      )}
                    </div>

                    <button
                      className="uk-button uk-button-default uk-button-small"
                      onClick={() => moveWindow(dateWindow.days)}
                      type="button"
                      uk-tooltip="Next"
                    >
                      <span uk-icon="chevron-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {(criteria.type || criteria.priority || criteria.property) && (
            <div className="hms-active-filters calendar-active-filters uk-margin-small-bottom">
              <span className="hms-active-filters-label">Filters:</span>
              {criteria.type && (
                <span className="hms-filter-chip">
                  Type: {selectedTypeLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("type", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.priority && (
                <span className="hms-filter-chip">
                  Priority: {selectedPriorityLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("priority", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.property && (
                <span className="hms-filter-chip">
                  Property: {selectedPropertyLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("property", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                type="button"
                className="hms-filter-chip-clear-all"
                onClick={clearAppliedFilters}
              >
                Clear all
              </button>
            </div>
          )}

          <div className="calendar-scroll">
          <div
            className="calendar-container"
            style={{
              width: `${STICKY_COL_WIDTH + days.length * DAY_COL_WIDTH}px`,
            }}
          >
            {/* Header row */}
            <div
              className="calendar-row calendar-header"
              style={{
                gridTemplateColumns: `${STICKY_COL_WIDTH}px repeat(${days.length}, ${DAY_COL_WIDTH}px)`,
              }}
            >
              <div
                className="calendar-cell calendar-cell-sticky"
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                }}
              >
                Property
              </div>
              {days.map((d) => (
                <div key={d} className="calendar-cell calendar-header-cell">
                  <div>
                    {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: "short",
                    })}
                  </div>
                  <div>
                    {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Rows */}
            {visibleHomes.map((home) => {
              const items = activitiesByHome[home.id] || [];

              // Lane allocation pass (also used to size the row).
              const lanesProbe = [];
              const probe = (start, end) => {
                for (let l = 0; ; l++) {
                  if (!lanesProbe[l])
                    lanesProbe[l] = Array(days.length).fill(false);
                  let ok = true;
                  for (let d = start; d < end; d++) {
                    if (lanesProbe[l][d]) {
                      ok = false;
                      break;
                    }
                  }
                  if (ok) {
                    for (let d = start; d < end; d++) lanesProbe[l][d] = true;
                    return l;
                  }
                }
              };
              const placements = items
                .map((activity) => {
                  const inISO = activity.checkIn
                    ? asYMD(activity.checkIn)
                    : asYMD(activity.date);
                  const outISO = activity.checkOut
                    ? asYMD(activity.checkOut)
                    : null;
                  if (!inISO || !overlapsWindow(inISO, outISO)) return null;
                  let s = indexInWindow(inISO);
                  let e = outISO ? indexInWindow(outISO) + 1 : s + 1;
                  if (e <= s) e = s + 1;
                  s = Math.max(0, Math.min(s, days.length));
                  e = Math.max(0, Math.min(e, days.length));
                  const lane = probe(s, e);
                  return { activity, s, e, lane, inISO, outISO };
                })
                .filter(Boolean);
              const laneCount = Math.max(1, lanesProbe.length || 1);
              const minRowHeight = propertyRowMinHeight(home);
              const taskAreaHeight = laneCount * LANE_HEIGHT;
              const rowHeight = Math.max(taskAreaHeight, minRowHeight);
              let gridTemplateRows;
              if (laneCount === 1) {
                gridTemplateRows = `${rowHeight}px`;
              } else if (taskAreaHeight >= minRowHeight) {
                gridTemplateRows = `repeat(${laneCount}, ${LANE_HEIGHT}px)`;
              } else {
                const extra = minRowHeight - taskAreaHeight;
                gridTemplateRows = `repeat(${laneCount - 1}, ${LANE_HEIGHT}px) ${LANE_HEIGHT + extra}px`;
              }

              return (
                <div
                  key={home.id}
                  className="calendar-row"
                  style={{
                    gridTemplateColumns: `${STICKY_COL_WIDTH}px repeat(${days.length}, ${DAY_COL_WIDTH}px)`,
                    gridTemplateRows,
                    minHeight: rowHeight,
                    "--lanes": laneCount,
                    "--sticky": `${STICKY_COL_WIDTH}px`,
                    "--day": `${DAY_COL_WIDTH}px`,
                    position: "relative",
                  }}
                  onClick={handleEmptyRowClick(home)}
                  onDragOver={(e) => {
                    if (!draggedTaskRef.current) return;
                    const t = draggedTaskRef.current.type;
                    if (t === "guest" || t === "owner") return;
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    const dragged = draggedTaskRef.current;
                    if (!dragged) return;
                    if (dragged.type === "guest" || dragged.type === "owner") {
                      draggedTaskRef.current = null;
                      return;
                    }
                    const idx = dayIndexFromClientX(
                      e,
                      e.currentTarget,
                      STICKY_COL_WIDTH,
                      DAY_COL_WIDTH,
                      days.length
                    );
                    const droppedDate = days[idx];
                    const draggedHomeId = dragged.homeId;
                    draggedTaskRef.current = null;
                    document.body.classList.remove("dragging-activity");
                    if (draggedHomeId !== home.id) return;
                    if (!droppedDate || droppedDate === dragged.date) return;
                    if (!dragged.isRecurring && !dragged.workOrderId) return;

                    void applyDraggedTaskDate(dragged, droppedDate);
                  }}
                >
                  {/* sticky property cell */}
                  <div
                    className="calendar-cell calendar-cell-sticky"
                    style={{ gridRow: `1 / span ${laneCount}` }}
                    title={
                      home.address
                        ? `${home.label} \u2014 ${home.address}`
                        : home.label
                    }
                  >
                    <div className="prop-id">{home.label}</div>
                    {home.address && (
                      <div className="secondary prop-address">{home.address}</div>
                    )}
                  </div>

                  {/* day cells (clickable for empty-cell add) */}
                  {days.map((day, dayIndex) => {
                    const isWeekend = [0, 6].includes(
                      new Date(`${day}T00:00:00`).getDay()
                    );
                    return (
                      <div
                        key={dayIndex}
                        style={{
                          gridColumn: dayIndex + 2,
                          gridRow: `1 / span ${laneCount}`,
                          cursor: "pointer",
                        }}
                        className={`calendar-cell calendar-content-cell ${
                          isWeekend ? "calendar-cell-weekend" : ""
                        }`}
                      />
                    );
                  })}

                  {/* pills */}
                  {placements.map((p, idx) => {
                    const { activity, s, e, lane, inISO, outISO } = p;
                    const gridRow = lane + 1;
                    const gridColStart = s + 2;
                    const gridColEnd = e + 2;
                    const startsInsideWindow =
                      inISO >= windowStartISO && inISO < windowEndISO;
                    const endsInsideWindow =
                      outISO &&
                      outISO > windowStartISO &&
                      outISO < windowEndISO;
                    const halfEligible = ["guest", "owner", "housekeeping"].includes(
                      activity.type
                    );
                    const halfStart =
                      halfEligible &&
                      startsInsideWindow &&
                      activity.checkIn &&
                      isAfternoon(activity.checkIn);
                    const halfEnd =
                      halfEligible &&
                      endsInsideWindow &&
                      activity.checkOut &&
                      isMorning(activity.checkOut);
                    const isTurnover =
                      activity.type === "housekeeping" &&
                      /turn.?over|turn.?clean/i.test(activity.label || "");
                    return (
                      <div
                        key={idx}
                        className={[
                          "calendar-item",
                          ACTIVITY_META[activity.type]?.className,
                          `calendar-status-${activity.status || "new"}`,
                          isAssignableActivity(activity) && !activity.assignee
                            ? "calendar-item-unassigned"
                            : "",
                          halfStart ? "half-start" : "",
                          halfEnd ? "half-end" : "",
                          isTurnover ? "is-turnover" : "",
                        ].join(" ")}
                        style={{
                          gridColumn: `${gridColStart} / ${gridColEnd}`,
                          gridRow,
                        }}
                        title={activity.label}
                        draggable={
                          activity.type !== "guest" && activity.type !== "owner"
                        }
                        onDragStart={(ev) => {
                          if (
                            activity.type !== "guest" &&
                            activity.type !== "owner"
                          ) {
                            clearPopupTimer();
                            setPopupData(null);
                            draggedTaskRef.current = activity;
                            if (ev.dataTransfer) {
                              ev.dataTransfer.effectAllowed = "move";
                              ev.dataTransfer.setData("text/plain", "activity");
                            }
                            document.body.classList.add("dragging-activity");
                          }
                        }}
                        onDragEnd={() => {
                          draggedTaskRef.current = null;
                          document.body.classList.remove("dragging-activity");
                        }}
                        onMouseEnter={handlePillHover(activity)}
                        onMouseLeave={handlePillLeave}
                        onClick={handlePillClick(activity)}
                      >
                        <img
                          src={ACTIVITY_META[activity.type]?.icon}
                          alt={activity.type}
                          className="calendar-item-icon"
                        />
                        <span className="calendar-item-text">
                          {activity.label}
                        </span>
                        {isAssignableActivity(activity) && !activity.assignee && (
                          <span
                            className="calendar-unassigned-indicator"
                            uk-tooltip="Unassigned"
                          >
                            !
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {popupData && (
              <div
                className="calendar-popup"
                onMouseEnter={clearPopupTimer}
                onMouseLeave={handlePillLeave}
                style={{
                  position: "fixed",
                  top: popupData.position.y,
                  left: popupData.position.x,
                  zIndex: 1100,
                }}
              >
                <button
                  type="button"
                  className="calendar-popup-close"
                  onClick={() => setPopupData(null)}
                  aria-label="Close"
                >
                  <img
                    src="/images/close.svg"
                    width="16"
                    height="16"
                    alt="Close"
                  />
                </button>

                <div className="calendar-popup-header">
                  <div className="calendar-popup-icon">
                    <img
                      src={
                        ACTIVITY_META[popupData.activity.type]?.popupIcon ||
                        ACTIVITY_META[popupData.activity.type]?.icon
                      }
                      alt={popupData.activity.type}
                      width="24"
                      height="24"
                    />
                  </div>
                  <div className="calendar-popup-title-block">
                    <div className="calendar-popup-type">
                      {ACTIVITY_META[popupData.activity.type]?.label || "Item"}
                    </div>
                    <h5>{popupData.activity.label}</h5>
                  </div>
                </div>

                {["guest", "owner"].includes(popupData.activity.type) && (
                  <div className="calendar-popup-details">
                    {popupData.activity.guestName && (
                      <div className="calendar-popup-row">
                        <span>Name</span>
                        <strong>{popupData.activity.guestName}</strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>Check-in / Check-out</span>
                      <strong>
                        {new Date(popupData.activity.checkIn).toLocaleDateString()} -{" "}
                        {new Date(popupData.activity.checkOut).toLocaleDateString()}
                      </strong>
                    </div>
                    <div className="calendar-popup-row">
                      <span>Property</span>
                      <strong>{propertyById[popupData.activity.homeId]?.label || ""}</strong>
                    </div>
                    {propertyById[popupData.activity.homeId]?.address && (
                      <div className="calendar-popup-row">
                        <span>Address</span>
                        <strong>{propertyById[popupData.activity.homeId].address}</strong>
                      </div>
                    )}
                    {popupData.activity.reservationId && (
                      <div className="calendar-popup-row">
                        <span>Reservation #</span>
                        <strong>{popupData.activity.reservationId}</strong>
                      </div>
                    )}
                    {popupData.activity.source && (
                      <div className="calendar-popup-row">
                        <span>Source</span>
                        <strong>{popupData.activity.source}</strong>
                      </div>
                    )}
                    {popupData.activity.formatted_phone && (
                      <div className="calendar-popup-row">
                        <span>Phone</span>
                        <strong>{popupData.activity.formatted_phone}</strong>
                      </div>
                    )}
                  </div>
                )}

                {!["guest", "owner"].includes(popupData.activity.type) && (
                  <div className="calendar-popup-details">
                    <div className="calendar-popup-row">
                      <span>Summary</span>
                      <strong>{popupData.activity.label}</strong>
                    </div>
                    {popupData.activity.date && (
                      <div className="calendar-popup-row">
                        <span>Date</span>
                        <strong>
                          {formatPopupDateTime(
                            popupData.activity.scheduledDate || popupData.activity.date
                          )}
                        </strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>Property</span>
                      <strong>{propertyById[popupData.activity.homeId]?.label || ""}</strong>
                    </div>
                    {propertyById[popupData.activity.homeId]?.address && (
                      <div className="calendar-popup-row">
                        <span>Address</span>
                        <strong>{propertyById[popupData.activity.homeId].address}</strong>
                      </div>
                    )}
                    {popupData.activity.status && (
                      <div className="calendar-popup-row">
                        <span>Status</span>
                        <strong>{titleizeValue(popupData.activity.status)}</strong>
                      </div>
                    )}
                    {popupData.activity.priority && (
                      <div className="calendar-popup-row">
                        <span>Priority</span>
                        <strong>{titleizeValue(popupData.activity.priority)}</strong>
                      </div>
                    )}
                    {popupData.activity.propertyManager && (
                      <div className="calendar-popup-row">
                        <span>Property Manager</span>
                        <strong>{popupData.activity.propertyManager}</strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>Assigned To</span>
                      <strong>{popupData.activity.assignee || "Unassigned"}</strong>
                    </div>
                    {popupData.activity.ticket_number && (
                      <div className="calendar-popup-row">
                        <span>#</span>
                        <strong>{popupData.activity.ticket_number}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="uk-text-meta uk-text-center uk-margin-small-top">
            Loading…
          </div>
        )}
        </div>
      </div>

      {/* Keep this mounted like /operations/tasks so UIkit, nested modals,
          antd dropdowns, and portals initialize against a stable offcanvas. */}
      <WorkOrderAdd
        showTaskType
        showRecurrence
        itemLabel="Task"
        taskTypeLabel="Type*"
        onSaved={load}
      />
      <WorkOrderDelete
        itemLabel="Task"
        closeEditOnDelete
        onDeleted={load}
      />
    </>
  );
};

export default WorkOrderCalendar;
